import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { forumApi } from '../api/forum';
import { UserContext } from '../context/UserContext';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbAlert from '../components/controls/HrnbAlert';
import { SkeletonCard } from '../components/controls/HrnbSkeleton';
import { BackIcon, ForwardIcon, CreateProfileIcon } from '../icons';
import '../css/pages/TagPage.css';
import PostCard from "../components/forum/PostCard";

const PAGE_SIZE = 12;

export default function TagPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);

    const [posts, setPosts] = useState([]);
    const [tag, setTag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);

    const abortControllerRef = useRef(null);

    const fetchTagInfo = useCallback(async () => {
        if (!slug) return;
        try {
            // Получаем информацию о теге через первый запрос постов
            // API не имеет отдельного endpoint для тега, поэтому используем getPostsByTag
        } catch (err) {
            console.error('[TagPage] Failed to fetch tag info:', err.message);
        }
    }, [slug]);

    const fetchPosts = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            setLoading(true);
            setError(null);

            const params = {
                page: currentPage - 1,
                size: PAGE_SIZE,
            };

            const data = await forumApi.getPostsByTag(slug, params, abortControllerRef.current.signal);
            
            // Пытаемся извлечь информацию о теге из ответа
            const tagInfo = data.tag || data.currentTag || {
                name: slug.replace(/-/g, ' '),
                slug: slug,
                postCount: data.totalElements || data.total || 0
            };
            setTag(tagInfo);
            
            setPosts(data.content || data.posts || data.results || []);
            setTotal(data.totalElements || data.total || 0);
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('[TagPage] Failed to fetch posts:', err.message);
            setError(`Не удалось загрузить посты с тегом #${slug}`);
        } finally {
            setLoading(false);
        }
    }, [slug, currentPage]);

    useEffect(() => {
        fetchTagInfo();
    }, [fetchTagInfo]);

    useEffect(() => {
        fetchPosts();
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchPosts]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleBack = () => {
        navigate('/forum');
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    const formattedTagName = tag?.name || slug?.replace(/-/g, ' ');

    return (
        <div className="tag-page">
            <div className="tag-page-header">
                <div className="tag-page-nav">
                    <HrnbButton variant="outline" onClick={handleBack} size="sm" icon={<BackIcon />}>
                        Назад к форуму
                    </HrnbButton>
                </div>
                <div className="tag-page-title-section">
                    <h1 className="tag-page-title">
                        <span className="tag-page-hash">#</span>
                        {formattedTagName}
                    </h1>
                    {tag?.postCount !== undefined && (
                        <p className="tag-page-count">
                            {tag.postCount} {getPlural(tag.postCount, ['пост', 'поста', 'постов'])}
                        </p>
                    )}
                </div>
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="tag-page-content">
                {loading ? (
                    <div className="tag-posts-grid">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                ) : posts.length > 0 ? (
                    <>
                        <div className="tag-posts-grid">
                            {posts.map((post) => (
                                <PostCard key={post.id} post={post} />
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="tag-page-pagination">
                                <HrnbButton
                                    variant="outline"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    icon={<BackIcon />}
                                >
                                    Назад
                                </HrnbButton>
                                <span className="pagination-info">
                                    Страница {currentPage} из {totalPages}
                                </span>
                                <HrnbButton
                                    variant="outline"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    icon={<ForwardIcon />}
                                >
                                    Вперёд
                                </HrnbButton>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="tag-page-empty">
                        <h3>Постов с этим тегом пока нет</h3>
                        <p>Будьте первым, кто создаст пост с тегом #{formattedTagName}</p>
                        {currentUser && (
                            <HrnbButton 
                                variant="primary" 
                                onClick={() => navigate('/forum', { state: { defaultTag: slug } })}
                                icon={<CreateProfileIcon />}
                            >
                                Создать пост
                            </HrnbButton>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function getPlural(number, forms) {
    const cases = [2, 0, 1, 1, 1, 2];
    const index = number % 100 > 4 && number % 100 < 20 ? 2 : cases[Number(number % 10) < 5 ? number % 10 : 5];
    return forms[index];
}
